import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';

interface StorePayment {
  id: string;
  date: string;
  source: 'student' | 'walk_in' | 'payment_link';
  customer: string | null;
  email: string | null;
  product: string;
  quantity: number;
  amount: number;
  discount: number;
  credit_applied: number;
  refunded: number;
  method: string;
  status: 'paid' | 'refunded' | 'partially_refunded';
  notes: string | null;
  record_type: 'stripe' | 'manual_payment' | 'store_sale';
}

// Full refunds only: anything with money not yet refunded can be refunded
const canRefund = (p: StorePayment) => p.status !== 'refunded' && p.amount - p.refunded > 0;

interface StorePaymentHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  getAuthToken: () => string | null;
}

const SOURCE_LABELS: Record<StorePayment['source'], string> = {
  student: 'Student',
  walk_in: 'In-person',
  payment_link: 'Payment link',
};

const SOURCE_STYLES: Record<StorePayment['source'], string> = {
  student: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  walk_in: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  payment_link: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
};

const PAGE_SIZE = 25;

const RANGES = [
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'Last 12 months' },
];

const formatMethod = (method: string) =>
  method.startsWith('card')
    ? method.replace(/^card/, 'Card').replace(/\b(visa|mastercard|amex|discover)\b/i, (b) => b[0].toUpperCase() + b.slice(1))
    : method.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function StorePaymentHistoryModal({ isOpen, onClose, getAuthToken }: StorePaymentHistoryModalProps) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [days, setDays] = useState(90);
  const [payments, setPayments] = useState<StorePayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'all' | StorePayment['source']>('all');
  const [refundTarget, setRefundTarget] = useState<StorePayment | null>(null);
  const [refunding, setRefunding] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${backendUrl}/api/stripe/store/payment-history?days=${days}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load payment history');
      setPayments(data.payments || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payment history');
    } finally {
      setLoading(false);
    }
  }, [backendUrl, days, getAuthToken]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen) {
      setRefundTarget(null);
      setNotice(null);
    }
  }, [isOpen]);

  const confirmRefund = async () => {
    if (!refundTarget) return;
    setRefunding(true);
    setRefundError(null);
    try {
      const res = await fetch(`${backendUrl}/api/stripe/store/refund`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({ sale_id: refundTarget.id, record_type: refundTarget.record_type }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Refund failed');
      const amount = (data.refunded_amount ?? refundTarget.amount - refundTarget.refunded).toFixed(2);
      setNotice(
        refundTarget.record_type === 'stripe'
          ? `Refunded $${amount} to ${refundTarget.customer || 'the customer'}'s card.`
          : `Marked $${amount} as refunded for ${refundTarget.customer || 'the customer'}.`
      );
      setRefundTarget(null);
      load();
    } catch (err) {
      setRefundError(err instanceof Error ? err.message : 'Refund failed');
    } finally {
      setRefunding(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return payments.filter(
      (p) =>
        (sourceFilter === 'all' || p.source === sourceFilter) &&
        (!q ||
          p.product.toLowerCase().includes(q) ||
          (p.customer ?? '').toLowerCase().includes(q) ||
          (p.email ?? '').toLowerCase().includes(q))
    );
  }, [payments, search, sourceFilter]);

  // Back to the first page whenever the list changes shape
  useEffect(() => {
    setPage(1);
  }, [search, sourceFilter, days]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  const netTotal = filtered.reduce((sum, p) => sum + p.amount - p.refunded, 0);
  const itemsSold = filtered.reduce((sum, p) => sum + (p.status === 'refunded' ? 0 : p.quantity), 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75 backdrop-blur-sm p-4">
      <div className="relative flex max-h-[92vh] w-full max-w-5xl flex-col rounded-xl bg-white shadow-2xl dark:bg-gray-800">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Store Payment History</h2>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              Product sales to students, in-person customers and payment links
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Filters + summary */}
        <div className="flex flex-col gap-3 border-b border-gray-200 px-6 py-3 dark:border-gray-700 md:flex-row md:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search product or customer…"
              className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as typeof sourceFilter)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            <option value="all">All sales</option>
            <option value="student">Students</option>
            <option value="walk_in">In-person</option>
            <option value="payment_link">Payment link</option>
          </select>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            {RANGES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>

        {!loading && !error && (
          <div className="flex gap-6 px-6 pt-3 text-sm">
            <span className="text-gray-500 dark:text-gray-400">
              Net total <span className="font-semibold text-gray-900 dark:text-white">${netTotal.toFixed(2)}</span>
            </span>
            <span className="text-gray-500 dark:text-gray-400">
              Items sold <span className="font-semibold text-gray-900 dark:text-white">{itemsSold}</span>
            </span>
            <span className="text-gray-500 dark:text-gray-400">
              Sales <span className="font-semibold text-gray-900 dark:text-white">{filtered.length}</span>
            </span>
          </div>
        )}

        {notice && (
          <div className="mx-6 mt-3 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 dark:border-green-800 dark:bg-green-900/20">
            <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-green-600 dark:text-green-400" />
            <p className="flex-1 text-sm text-green-700 dark:text-green-300">{notice}</p>
            <button type="button" onClick={() => setNotice(null)} className="text-green-600 hover:text-green-800 dark:text-green-400">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 py-3">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
            </div>
          ) : error ? (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
              <ExclamationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          ) : filtered.length === 0 ? (
            <p className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">No store sales in this period.</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
              <thead>
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Product</th>
                  <th className="py-2 pr-4">Customer</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Method</th>
                  <th className="py-2 text-right">Amount</th>
                  <th className="py-2 pl-4"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {pageRows.map((p) => (
                  <tr key={p.id} className="align-top">
                    <td className="whitespace-nowrap py-3 pr-4 text-gray-500 dark:text-gray-400">
                      {new Date(p.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {p.product}
                        {p.quantity > 1 && <span className="text-gray-500 dark:text-gray-400"> × {p.quantity}</span>}
                      </div>
                      {p.notes && <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{p.notes}</div>}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="text-gray-900 dark:text-white">{p.customer || '—'}</div>
                      {p.email && <div className="text-xs text-gray-500 dark:text-gray-400">{p.email}</div>}
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${SOURCE_STYLES[p.source]}`}>
                        {SOURCE_LABELS[p.source]}
                      </span>
                    </td>
                    <td className="whitespace-nowrap py-3 pr-4 text-gray-600 dark:text-gray-300">{formatMethod(p.method)}</td>
                    <td className="whitespace-nowrap py-3 text-right">
                      <div className={`font-medium ${p.status === 'refunded' ? 'text-gray-400 line-through' : 'text-gray-900 dark:text-white'}`}>
                        ${p.amount.toFixed(2)}
                      </div>
                      {p.status !== 'paid' && (
                        <div className="text-xs text-red-600 dark:text-red-400">
                          {p.status === 'refunded' ? 'Refunded' : `$${p.refunded.toFixed(2)} refunded`}
                        </div>
                      )}
                      {p.discount > 0 && (
                        <div className="text-xs text-emerald-600 dark:text-emerald-400">−${p.discount.toFixed(2)} discount</div>
                      )}
                      {p.credit_applied > 0 && (
                        <div className="text-xs text-emerald-600 dark:text-emerald-400">${p.credit_applied.toFixed(2)} credit</div>
                      )}
                    </td>
                    <td className="whitespace-nowrap py-3 pl-4 text-right">
                      {canRefund(p) && (
                        <button
                          type="button"
                          onClick={() => {
                            setRefundError(null);
                            setRefundTarget(p);
                          }}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-red-600 ring-1 ring-inset ring-red-200 hover:bg-red-50 dark:text-red-400 dark:ring-red-800 dark:hover:bg-red-900/20"
                        >
                          <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                          Refund
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!loading && !error && filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3 text-sm dark:border-gray-700">
            <span className="text-gray-500 dark:text-gray-400">
              Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage <= 1}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                Previous
              </button>
              <span className="text-gray-500 dark:text-gray-400">
                Page {currentPage} of {pageCount}
              </span>
              <button
                type="button"
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage >= pageCount}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Refund confirmation */}
      {refundTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Refund ${(refundTarget.amount - refundTarget.refunded).toFixed(2)}?
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              {refundTarget.product}
              {refundTarget.quantity > 1 ? ` × ${refundTarget.quantity}` : ''}
              {refundTarget.customer ? ` · ${refundTarget.customer}` : ''}
            </p>
            <div className="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-300">
              {refundTarget.record_type === 'stripe' ? (
                <p>
                  The full amount goes back to the customer's card through Stripe. This can't be undone.
                </p>
              ) : (
                <p>
                  This marks the sale as refunded. Give the customer their{' '}
                  {formatMethod(refundTarget.method).toLowerCase()} payment back in person.
                </p>
              )}
              <p className="text-gray-500 dark:text-gray-400">
                Stock isn't returned automatically. Adjust inventory on the product if needed.
              </p>
            </div>
            {refundError && (
              <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                <ExclamationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                <p className="text-sm text-red-700 dark:text-red-300">{refundError}</p>
              </div>
            )}
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setRefundTarget(null)}
                disabled={refunding}
                className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRefund}
                disabled={refunding}
                className="flex-1 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {refunding ? 'Refunding…' : 'Refund'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
